import { StrategyExpanded, StrategySortType, useBsPrincipalTokens } from "@bridgesplit/abf-react";
import {
    Column,
    EmptyPlaceholder,
    EmptyPlaceholderProps,
    LabelWrapper,
    MEDIA,
    MediaStack,
    Pagination,
    Row,
    Select,
    repeatElement,
    usePopover
} from "@bridgesplit/ui";
import { useLocation } from "react-router-dom";
import { EmptyPromptType, useEmptyPrompts } from "app/utils";
import { COPY, PORTFOLIO_BASE } from "app/constants";
import { DispatchType } from "@bridgesplit/react";
import { useLenderStrategiesPagination } from "app/api/strategy";

import StrategyCard from "./StrategyCard";
import { FilterButtonPopover, SortDirectionButton, TokenSelect } from "../../common";

const BREAKPOINT = MEDIA.LG;

type PaginationType = ReturnType<typeof useLenderStrategiesPagination>;

export default function StrategyCards({
    strategies,
    defaultPrincipalMint,
    pagination,
    totalStrategies
}: {
    strategies: StrategyExpanded[] | undefined;
    defaultPrincipalMint?: string;
    pagination: PaginationType;
    totalStrategies: number | undefined;
}) {
    return (
        <Column spacing={2}>
            <PaginationControls pagination={pagination} defaultPrincipalMint={defaultPrincipalMint} />

            {strategies
                ? strategies?.map((strategy) => (
                      <StrategyCard query={BREAKPOINT} key={strategy.strategy.id} strategyExpanded={strategy} />
                  ))
                : repeatElement(
                      <StrategyCard query={BREAKPOINT} strategyExpanded={undefined} />,
                      pagination.params.pageSize
                  )}

            {strategies?.length === 0 && (
                <Empty
                    emptyText={(() => {
                        if (!strategies?.length) return `You don't have any ${COPY.STRATEGY_TERM_PLURAL.toLowerCase()}`;
                        return `No matching ${COPY.STRATEGY_TERM_PLURAL.toLowerCase()}`;
                    })()}
                />
            )}
            {!!pagination && !!totalStrategies && totalStrategies > pagination.params.pageSize && (
                <Pagination
                    count={totalStrategies}
                    rowsPerPage={pagination.params.pageSize}
                    page={pagination.params.page}
                    setPage={pagination.setPage}
                />
            )}
        </Column>
    );
}

function PaginationControls({
    pagination,
    defaultPrincipalMint
}: {
    pagination: PaginationType;
    defaultPrincipalMint?: string;
}) {
    const {
        params: { sortType, sortDirection },
        setSortType,
        setSortDirection,
        setPage
    } = pagination;

    const sortOptions = getSortOptions();

    return (
        <MediaStack query={BREAKPOINT} spaceBetweenRow spacing={1}>
            <Filters
                principalMint={defaultPrincipalMint ?? pagination.params.principalMints?.[0]}
                setPrincipalMint={(mint) => {
                    setPage(0);
                    pagination.setPrincipalMint(mint);
                }}
            />

            <Row spacing={1} sx={{ width: "100%", justifyContent: "flex-end" }}>
                <SortDirectionButton
                    direction={sortDirection}
                    setDirection={(sortDirection) => {
                        setPage(0);
                        setSortDirection(sortDirection);
                    }}
                />

                <Select
                    sx={{ [BREAKPOINT.above]: { maxWidth: "200px" } }}
                    value={sortType}
                    setValue={(sort) => {
                        setPage(0);
                        setSortType(sort);
                    }}
                    options={sortOptions}
                />
            </Row>
        </MediaStack>
    );
}

function getSortOptions() {
    return [
        { value: StrategySortType.NetAssetValue, label: "Net asset value" },
        { value: StrategySortType.RecentlyCreated, label: "Recently created" }
    ];
}

function Filters({
    principalMint,
    setPrincipalMint
}: {
    principalMint: string | undefined;
    setPrincipalMint: DispatchType<string | undefined>;
}) {
    const { tokens } = useBsPrincipalTokens();
    const popoverProps = usePopover();

    return (
        <FilterButtonPopover reset={() => setPrincipalMint(undefined)} popoverProps={popoverProps}>
            <LabelWrapper label="Token">
                <TokenSelect tokens={tokens} value={principalMint} setValue={setPrincipalMint} />
            </LabelWrapper>
        </FilterButtonPopover>
    );
}

function Empty({ emptyText }: { emptyText: string }) {
    const getPrompt = useEmptyPrompts();
    const prompt = getPrompt(EmptyPromptType.Lend, emptyText);

    const location = useLocation();
    const promptWithCta: EmptyPlaceholderProps = {
        ...prompt,
        cta: location.pathname.includes(PORTFOLIO_BASE) ? prompt.cta : undefined
    };

    return <EmptyPlaceholder {...promptWithCta} />;
}
